// The console along the bottom: load errors, eval results and VM events. A few lines at a time;
// they fade out after a while, errors more slowly.
import type { SourceError } from './sim.ts';

const MAX_ENTRIES = 6;
const FADE_AFTER_MS = 8000;
const ERROR_FADE_AFTER_MS = 20000;

export type Tone = 'value' | 'running' | 'defined' | 'error' | 'warning' | 'event' | 'note';

export class Log {
  private el: HTMLElement;

  constructor(el: HTMLElement) {
    this.el = el;
  }

  /** `code` marks lines about the code in the editor, which clearCodeErrors() removes. */
  print(tone: Tone, text: string, code = false): void {
    const entry = document.createElement('div');
    entry.className = `entry ${tone}`;
    if (code) entry.dataset.code = '';
    entry.textContent = text;
    this.el.append(entry);
    while (this.el.childElementCount > MAX_ENTRIES) this.el.firstElementChild?.remove();
    const wait = tone === 'error' ? ERROR_FADE_AFTER_MS : FADE_AFTER_MS;
    setTimeout(() => {
      entry.classList.add('gone');
      entry.addEventListener('transitionend', () => entry.remove(), { once: true });
      // No transition runs when reduced motion is on; remove it anyway.
      setTimeout(() => entry.remove(), 1500);
    }, wait);
  }

  /** A line with a button on it, e.g. offering a download. */
  action(tone: Tone, text: string, label: string, run: () => void): void {
    this.print(tone, text);
    const entry = this.el.lastElementChild;
    if (!entry) return;
    const button = document.createElement('button');
    button.textContent = label;
    button.addEventListener('click', run);
    entry.append(' ', button);
    entry.classList.add('has-action');
  }

  /** An error in the code, with its position. */
  error(e: SourceError, prefix = ''): void {
    const at = e.line > 0 ? `${e.line}:${e.col} ` : '';
    this.print('error', `${prefix}${at}${e.message}`, true);
  }

  /** A warning about the code, with its position. */
  warn(e: SourceError): void {
    const at = e.line > 0 ? `${e.line}:${e.col} ` : '';
    this.print('warning', `${at}${e.message}`);
  }

  /** Drops errors about the code once it loads cleanly again. Other errors stay up. */
  clearCodeErrors(): void {
    for (const el of this.el.querySelectorAll('.entry.error[data-code]')) el.remove();
  }
}
